import * as braze from "@braze/web-sdk";
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Preferences } from "@capacitor/preferences";
import { getTheme } from "@clipboard-health/ui-theme";
import { datadogRum } from "@datadog/browser-rum";
import { createAnimation, setupConfig } from "@ionic/core";
import { IonApp } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import {
  EventInterceptionPlugin,
  FlagOverridePlugin,
  useLaunchDarklyToolbar,
} from "@launchdarkly/toolbar";
import { ThemeProvider } from "@mui/material";
import { AppV2PublicRoutes } from "@src/appV2";
import { CbhQueryClientProvider } from "@src/appV2/api/QueryProvider";
import { useAppShortcuts } from "@src/appV2/App/AppShortcuts/useAppShortcuts";
import { EnvironmentRibbon } from "@src/appV2/App/EnvironmentRibbon/EnvironmentRibbon";
import { OutageBanner } from "@src/appV2/App/OutageBanner/OutageBanner";
import { AppStateProvider } from "@src/appV2/App/useAppState";
import { useSetPreferredPersistenceForAuth } from "@src/appV2/App/useSetPreferredPersistenceForAuth/useSetPreferredPersistenceForAuth";
import { AuthHeaderProvider } from "@src/appV2/Auth/cognito/useAuthHeaderProvider";
import { ImpersonationProvider } from "@src/appV2/Auth/Impersonation/context";
import { ImpersonationBanner } from "@src/appV2/Auth/Impersonation/ImpersonationBanner";
import { useDefaultAuthScreen } from "@src/appV2/Auth/LoginSignUp/hooks/useDefaultAuthScreen";
import { OfflineIndicatorContainer } from "@src/appV2/Connectivity/OfflineIndicatorContainer";
import { MobileConsole } from "@src/appV2/Debug/MobileConsole";
import {
  EmbeddedModeProvider,
  EmbeddedPlacementRedirect,
  EmbeddedRouteSync,
} from "@src/appV2/EmbeddedMode/EmbeddedModeContext";
import { environmentConfig } from "@src/appV2/environment";
import { isDevelopmentNodeEnvironment } from "@src/appV2/environment/utils";
import { LD_USER_KIND } from "@src/appV2/FeatureFlags/cbhUserContext";
import { KnockTokenProvider } from "@src/appV2/Knock/context";
import { LegacyStateProvider } from "@src/appV2/LegacyStateBridge/context";
import {
  MuiBackdropProvider,
  MuiSnackBarProvider,
  isAndroidPlatform,
  isCapacitorPlatform,
} from "@src/appV2/lib";
import { GoogleMapsSdkProvider } from "@src/appV2/lib/GoogleMaps/context";
import { TimersProvider } from "@src/appV2/lib/utils/useTimers";
import { MaterialUiProviders } from "@src/appV2/mui";
import { setupPdfViewer } from "@src/appV2/PdfViewer/setupPdfViewer";
import { LockScreenOrientation } from "@src/appV2/ScreenOrientation";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ZendeskSDK } from "capacitor-zendesk-sdk";
import "./app.scss";
// Rule disabled since this is where we initialize LaunchDarkly
// eslint-disable-next-line no-restricted-imports
import { LDEvaluationDetail, withLDProvider } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";
import { Provider, useDispatch } from "react-redux";
import { Route } from "react-router-dom";
import { ErrorBoundary } from "src/appV2/Errors/ErrorBoundary";
import { IonAppProvider, useIonAppContext } from "src/appV2/ionic";

import { useForceAppResetOnResume } from "../hooks/useForceAppResetOnResume";
import { GoogleMapProvider } from "./context/googleMapContext";
import { PopupAlerts } from "./popups";
import { AppRoutes } from "./routing";
import { GlobalRouterPath } from "./routing/constant/globalRoute";
import { store } from "./store";
import { useSession } from "./store/helperHooks";
import { ActionType } from "./store/session";
import { useOtaUpdate } from "./useOtaUpdate";
import { useWhiteScreenDetection } from "./useWhiteScreenDetection/useWhiteScreenDetection";
import { useWorkerAppRebrandingSync } from "../appV2/redesign/Rebranding/useWorkerAppRebrandingSync";

if (isCapacitorPlatform()) {
  const isAndroid = isAndroidPlatform();

  void ZendeskSDK.initialize({
    channelKey: isAndroid
      ? environmentConfig.REACT_APP_ZENDESK_SDK_CHANNEL_KEY_ANDROID
      : environmentConfig.REACT_APP_ZENDESK_SDK_CHANNEL_KEY_IOS,
  });
}

void Preferences.migrate();

/**
 * pdf.worker.min.mjs is copied from node_modules to our public folder
 * Check out CopyWebpackPlugin configuration in webpack.config.js for more info
 *
 * We use REACT_APP_BASE_PATH to prefix the worker URL so it resolves correctly
 * in preview environments where assets are served under a subpath (e.g. /pr-123/).
 */
const rawBasePath = process.env.REACT_APP_BASE_PATH || "/";
const pdfWorkerBasePath = rawBasePath.endsWith("/") ? rawBasePath : `${rawBasePath}/`;
setupPdfViewer(`${pdfWorkerBasePath}assets/scripts/pdf.worker.min.mjs`);

if (
  !braze.initialize(environmentConfig.REACT_APP_HCP_WEB_BRAZE_KEY as string, {
    baseUrl: environmentConfig.REACT_APP_HCP_WEB_BRAZE_SDK_ENDPOINT as string,
  })
) {
  throw new Error("Braze web SDK initialization failed");
}
/**
 * FIXME Replace IonReactRouter and this Ionic config once all routing and components move to `react-router-dom`
 * Technically, we don't need to add `setupConfig`, but `IonReactRouter` appears to sometimes wait
 * for animations to complete.
 */
setupConfig({
  mode: "ios",
  navAnimation: () => {
    /**
     * A dummy animation to speed up the routing without odd transition delays.
     */
    return createAnimation();
  },
});

const areDevToolsEnabled = (localStorage.getItem("areDevToolsVisible") ?? "true") === "true";

const flagOverridePlugin = new FlagOverridePlugin();
const eventInterceptionPlugin = new EventInterceptionPlugin();

const MainApp = () => {
  // Initialize LaunchDarkly toolbar in development mode only
  useLaunchDarklyToolbar({
    flagOverridePlugin,
    eventInterceptionPlugin,
    position: "bottom-right",
    enabled: isDevelopmentNodeEnvironment() && areDevToolsEnabled,
  });

  const session = useSession();
  const dispatch = useDispatch();

  useOtaUpdate();
  useWhiteScreenDetection();
  useForceAppResetOnResume();
  useWorkerAppRebrandingSync();
  useSetPreferredPersistenceForAuth();
  useAppShortcuts({ isAuthorized: session.isAuthorized });

  const { ionAppRef } = useIonAppContext();
  const { setDefaultToLogin } = useDefaultAuthScreen();

  // Fires on both login and app resumption
  useEffect(() => {
    if (session.isAuthorized) {
      setDefaultToLogin(true);
    }
  }, [session.isAuthorized, setDefaultToLogin]);

  return (
    <IonApp ref={ionAppRef}>
      <LockScreenOrientation />
      <IonReactRouter basename={process.env.REACT_APP_BASE_PATH || "/"}>
        <ImpersonationBanner />
        <EmbeddedPlacementRedirect />
        <EmbeddedRouteSync />
        <KnockTokenProvider userId={session.userId}>
          <MuiBackdropProvider>
            <MuiSnackBarProvider>
              <ThemeProvider theme={getTheme()}>
                <OutageBanner />
              </ThemeProvider>
              <AppStateProvider>
                <LegacyStateProvider
                  session={session}
                  setIsSignUp={(authModeIsSignUp) => {
                    localStorage.setItem("isSignup", authModeIsSignUp ? "true" : "false");
                    dispatch({
                      type: ActionType.SET_IS_SIGNUP,
                      data: {
                        isSignup: authModeIsSignUp,
                      },
                    });
                  }}
                >
                  <AppRoutes />
                </LegacyStateProvider>
              </AppStateProvider>
              <div className="cbhAppV2">
                <Route path={GlobalRouterPath.APP_V2}>
                  <LegacyStateProvider
                    session={session}
                    setIsSignUp={(authModeIsSignUp) => {
                      /**
                       * Legacy code uses localStorage in several places to propagate `isSignup` state
                       * This implicit binding is fragile, since it binds a hidden global browser state throughout
                       * the app.
                       * Note also, since the property lingers, it is quite possible for an existing user to
                       * encounter this value on a shared device.
                       * Note also, This won't work when users open the app across devices.
                       * For example, if they sign up on a desktop, and then complete the flow on
                       * their phone.
                       */
                      localStorage.setItem("isSignup", authModeIsSignUp ? "true" : "false");
                      dispatch({
                        type: ActionType.SET_IS_SIGNUP,
                        data: {
                          isSignup: authModeIsSignUp,
                        },
                      });
                    }}
                  >
                    <AppV2PublicRoutes />
                  </LegacyStateProvider>
                </Route>
              </div>
              <div className="cbhAppV1">
                <PopupAlerts />
              </div>
              <EnvironmentRibbon />
              <MobileConsole />
              <OfflineIndicatorContainer />
            </MuiSnackBarProvider>
          </MuiBackdropProvider>
        </KnockTokenProvider>
      </IonReactRouter>

      {areDevToolsEnabled && <ReactQueryDevtools initialIsOpen={false} />}
    </IonApp>
  );
};

const App = () => {
  return (
    <MaterialUiProviders>
      <EmbeddedModeProvider>
        <ImpersonationProvider>
          <ErrorBoundary>
            <GoogleMapsSdkProvider>
              <GoogleMapProvider>
                <Provider store={store}>
                  <CbhQueryClientProvider>
                    <AuthHeaderProvider>
                      <IonAppProvider>
                        <TimersProvider>
                          <MainApp />
                        </TimersProvider>
                      </IonAppProvider>
                    </AuthHeaderProvider>
                  </CbhQueryClientProvider>
                </Provider>
              </GoogleMapProvider>
            </GoogleMapsSdkProvider>
          </ErrorBoundary>
        </ImpersonationProvider>
      </EmbeddedModeProvider>
    </MaterialUiProviders>
  );
};

export default withLDProvider({
  clientSideID: environmentConfig.REACT_APP_LAUNCH_DARKLY_CLIENT_SIDE_ID,
  context: { anonymous: true, kind: LD_USER_KIND },
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  options: {
    evaluationReasons: true,
    plugins:
      isDevelopmentNodeEnvironment() && areDevToolsEnabled
        ? [flagOverridePlugin, eventInterceptionPlugin]
        : [],
    inspectors: [
      {
        type: "flag-used",
        name: "flag-inspector",
        method: async (key: string, detail: LDEvaluationDetail) => {
          datadogRum.addFeatureFlagEvaluation(key, detail.value);
        },
      },
    ],
  },
})(App);
